import React, { useState, useRef, useCallback } from "react";
import api from "@/lib/api";
import { formatIDR } from "@/lib/format";
import { Field } from "@/components/ui-helpers";
import { CategoryCombobox } from "@/components/CategoryCombobox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Camera, ImagePlus, Loader2, Trash2, Sparkles, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

// Downscale an image file to a JPEG data URL (max ~1400px) to keep payloads small.
function fileToCompressedDataUrl(file, maxSide = 1400, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function ScreenshotImportDialog({ open, onOpenChange, trips, selectedTripId, categories, onCreate, onCreated }) {
  const firstTrip = selectedTripId && selectedTripId !== "all" ? selectedTripId : (trips[0]?.id || "");
  const [tripId, setTripId] = useState(firstTrip);
  const [images, setImages] = useState([]);      // data URLs (compressed)
  const [rows, setRows] = useState([]);          // extracted, editable
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const trip = trips.find((t) => t.id === tripId) || trips[0];

  const reset = useCallback(() => { setImages([]); setRows([]); setExtracting(false); setSaving(false); }, []);

  const handleOpenChange = (v) => { if (!v) reset(); onOpenChange(v); };

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const room = 20 - images.length;
    if (room <= 0) { toast.error("Maksimal 20 screenshot"); return; }
    const take = files.slice(0, room);
    try {
      const urls = await Promise.all(take.map((f) => fileToCompressedDataUrl(f)));
      setImages((prev) => [...prev, ...urls]);
    } catch { toast.error("Gagal membaca gambar"); }
    if (fileRef.current) fileRef.current.value = "";
  };

  const removeImage = (i) => setImages((prev) => prev.filter((_, idx) => idx !== i));

  const extract = async () => {
    if (!images.length) return toast.error("Unggah minimal 1 screenshot");
    setExtracting(true);
    try {
      const { data } = await api.post("/products/import-from-screenshots", { images });
      const mapped = (data.items || []).map((it) => ({
        include: true,
        name: it.name || "",
        brand: it.brand || "",
        aud_price: it.price_aud != null ? String(it.price_aud) : "",
        description: it.description || "",
        estimated_weight: it.estimated_weight || "",
        category: "",
        photo: it.photo || images[it.index] || null,
        missing: it.missing || [],
        warnings: it.warnings || [],
      }));
      setRows(mapped);
      const okCount = mapped.filter((r) => !r.missing.length).length;
      toast.success(`${mapped.length} produk terbaca (${okCount} lengkap). Periksa lalu simpan.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal mengekstrak screenshot");
    } finally { setExtracting(false); }
  };

  const setRow = (i, k, v) => setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));

  const onRowPhoto = async (i, e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try { const url = await fileToCompressedDataUrl(f); setRow(i, "photo", url); }
    catch { toast.error("Gagal membaca gambar"); }
    if (e.target) e.target.value = "";
  };

  const saveAll = async () => {
    const selected = rows.filter((r) => r.include);
    if (!selected.length) return toast.error("Pilih minimal 1 produk untuk disimpan");
    const invalid = selected.filter((r) => !r.name.trim() || !r.aud_price);
    if (invalid.length) return toast.error("Lengkapi nama & harga AUD pada produk terpilih");
    if (!tripId) return toast.error("Pilih trip");
    setSaving(true);
    let ok = 0, fail = 0;
    for (const r of selected) {
      const payload = {
        trip_id: tripId,
        name: r.name.trim(),
        brand: r.brand.trim(),
        category: r.category || "",
        description: r.description.trim(),
        aud_price: String(r.aud_price),
        estimated_weight: r.estimated_weight || "",
        availability: "Ditemukan",
        quantity_available: 1,
        marketed_quantity: 1,
        photo: r.photo || null,
        margin_percent: trip?.default_margin_percent || "",
        jasa_titip_type: trip?.default_jasa_titip_type || "fixed",
        jasa_titip_value: trip?.default_jasa_titip_value || "",
        delivery_fee: trip?.default_delivery_fee || "",
        rounding_method: trip?.default_rounding_method || "none",
        additional_fee: "0", discount: "0", custom_step: 0,
      };
      try { await api.post("/products", payload); ok += 1; } catch { fail += 1; }
    }
    setSaving(false);
    if (ok) toast.success(`${ok} produk tersimpan${fail ? `, ${fail} gagal` : ""}`);
    else toast.error("Gagal menyimpan produk");
    if (ok) { onCreated?.(); handleOpenChange(false); }
  };

  const selectedCount = rows.filter((r) => r.include).length;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="screenshot-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Camera className="h-5 w-5 text-primary" />Impor dari Screenshot
          </DialogTitle>
        </DialogHeader>

        <div className="rounded-xl border border-dashed border-border bg-secondary/40 p-3 space-y-2">
          <p className="text-sm text-muted-foreground">
            Unggah screenshot halaman produk (mis. Coles/Woolworths). AI membaca nama, brand, harga (dibulatkan ke atas)
            &amp; membuat deskripsi singkat gaya influencer. Bisa banyak sekaligus.
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Trip">
              <Select value={tripId} onValueChange={setTripId}>
                <SelectTrigger data-testid="screenshot-trip-select"><SelectValue placeholder="Pilih trip" /></SelectTrigger>
                <SelectContent>{trips.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Screenshot">
              <input ref={fileRef} type="file" accept="image/*" multiple onChange={onFiles} className="hidden" data-testid="screenshot-file-input" />
              <Button type="button" variant="outline" className="w-full" onClick={() => fileRef.current?.click()} data-testid="screenshot-pick-button">
                <ImagePlus className="h-4 w-4 mr-1.5" />Pilih Gambar ({images.length}/20)
              </Button>
            </Field>
          </div>

          {images.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-1" data-testid="screenshot-thumbs">
              {images.map((src, i) => (
                <div key={i} className="relative h-16 w-16 rounded-lg overflow-hidden border border-border group">
                  <img src={src} alt={`ss-${i}`} className="h-full w-full object-cover" />
                  <button onClick={() => removeImage(i)} data-testid="screenshot-remove-thumb"
                    className="absolute inset-0 bg-background/70 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <Button type="button" onClick={extract} disabled={extracting || !images.length} data-testid="screenshot-extract-button">
            {extracting ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />Membaca…</> : <><Sparkles className="h-4 w-4 mr-1.5" />Ekstrak dengan AI</>}
          </Button>
        </div>

        {rows.length > 0 && (
          <div className="space-y-3">
            <p className="text-sm font-medium">Tinjau &amp; edit ({selectedCount} dipilih)</p>
            {rows.map((r, i) => (
              <div key={i} className={`rounded-xl border p-3 flex gap-3 ${r.include ? "border-border bg-card" : "border-border bg-secondary/30 opacity-70"}`} data-testid="screenshot-review-row">
                <div className="flex flex-col items-center gap-2 shrink-0">
                  <label className="relative h-20 w-20 rounded-lg overflow-hidden bg-secondary border border-border cursor-pointer group block" title="Klik untuk ganti foto">
                    {r.photo ? <img src={r.photo} alt={r.name} className="h-full w-full object-cover" /> : <span className="h-full w-full flex items-center justify-center text-muted-foreground text-xs">Foto</span>}
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => onRowPhoto(i, e)} data-testid="screenshot-row-photo-input" />
                    <span className="absolute inset-x-0 bottom-0 py-0.5 text-[9px] text-center bg-background/75 opacity-0 group-hover:opacity-100 transition-opacity">Ganti foto</span>
                  </label>
                  <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                    <Checkbox checked={r.include} onCheckedChange={(v) => setRow(i, "include", !!v)} data-testid="screenshot-row-include" />
                    Simpan
                  </label>
                </div>
                <div className="flex-1 min-w-0 grid sm:grid-cols-2 gap-2">
                  <Field label="Nama"><Input value={r.name} onChange={(e) => setRow(i, "name", e.target.value)} data-testid="screenshot-row-name" className={r.missing.includes("nama") ? "ring-2 ring-[hsl(var(--warning))]" : ""} /></Field>
                  <Field label="Harga AUD"><Input type="number" step="0.01" value={r.aud_price} onChange={(e) => setRow(i, "aud_price", e.target.value)} data-testid="screenshot-row-price" className={r.missing.includes("harga") ? "ring-2 ring-[hsl(var(--warning))]" : ""} /></Field>
                  <Field label="Brand"><Input value={r.brand} onChange={(e) => setRow(i, "brand", e.target.value)} /></Field>
                  <Field label="Berat (kg)"><Input type="number" step="0.01" value={r.estimated_weight} onChange={(e) => setRow(i, "estimated_weight", e.target.value)} data-testid="screenshot-row-weight" /></Field>
                  <Field label="Kategori"><CategoryCombobox value={r.category} categories={categories} onChange={(v) => setRow(i, "category", v)} onCreate={onCreate} testId={`ss-category-${i}`} /></Field>
                  <div className="sm:col-span-2"><Field label="Deskripsi"><Textarea rows={2} value={r.description} onChange={(e) => setRow(i, "description", e.target.value)} data-testid="screenshot-row-desc" /></Field></div>
                  {(r.warnings.length > 0 || r.missing.length > 0) && (
                    <div className="sm:col-span-2 flex items-start gap-1.5 text-[11px] text-[hsl(var(--warning))]">
                      <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                      <span>{[...r.warnings, r.missing.length ? `Lengkapi: ${r.missing.join(", ")}` : ""].filter(Boolean).join(" · ")}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="sticky bottom-0 z-10 -mx-6 -mb-6 px-6 py-3 bg-background/92 backdrop-blur border-t border-border flex items-center justify-between gap-2 mt-2">
          <span className="text-xs text-muted-foreground">{trip ? `Kurs ${Number(trip.exchange_rate).toLocaleString("id-ID")} · harga jual dihitung dari default trip` : ""}</span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => handleOpenChange(false)}>Batal</Button>
            <Button onClick={saveAll} disabled={saving || !rows.length} data-testid="screenshot-save-all-button">
              {saving ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />Menyimpan…</> : `Simpan semua (${selectedCount})`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
